import Spinner from 'react16-spinjs';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import {
    getShouldInvalidateResult,
    AUDIO_SAVING_MESSAGE,
    INVALIDATE_RESULT_MESSAGE_WITH_REMINDER,
    VR_RESULT_PROCESSING_TITLE,
    VR_RESULT_PROCESSING_MESSAGE,
    UNABLE_TO_DETECT_READING_MESSAGE,
    VR_RESULT_PROCESSING_TIMEOUT_TITLE,
    VR_RESULT_PROCESSING_TIMOUT_MESSAGE,
    VR_ERROR_REASONS,
    SOMETHING_WENT_WRONG_MESSAGE,
    INVALIDATE_RESULT_MESSAGE,
} from '../../../constants/error';
import { SPINNER, COMMON_HELP_POPUP_BUTTON_CLASSNAME } from '../../../constants/components';
import LogoutButton from '../../error/LogoutButton';
import HelpPopup from './HelpPopup';
import { redirectToARwithError } from '../../../trc/troUtils';

const SPINNER_ICON = (
    <div className="help-popup-spinner-container">
        <Spinner config={SPINNER.DARK_SMALL} />
    </div>
);

const VRHelpPopup = ({
    logUserOut,
    resultsVRStatuses,
    resultVRErrorReasons,
    isAudioSavingInProgress,
    isVrResultProcessingTimeout,
    isExternalURLSourcePresent,
    isBenchmarkModeEnabled,
    trackTroError,
    bookId,
    bookTitle,
    trackTroVrPopup
}) => {
    useEffect(() => {
        const getPopupProblemText = () => {
            if (isVrResultProcessingTimeout) return VR_RESULT_PROCESSING_TIMOUT_MESSAGE;
            if (isAudioSavingInProgress) return AUDIO_SAVING_MESSAGE;
            if (getShouldInvalidateResult(resultsVRStatuses, resultVRErrorReasons)) {
                const isVRResultUnknownError = resultVRErrorReasons.includes(VR_ERROR_REASONS.UNKNOWN);
                return isVRResultUnknownError ? SOMETHING_WENT_WRONG_MESSAGE : UNABLE_TO_DETECT_READING_MESSAGE;
            }
            return VR_RESULT_PROCESSING_MESSAGE;
        };
        if (!isExternalURLSourcePresent) trackTroVrPopup(getPopupProblemText(), bookId, bookTitle);
    }, []);

    if (isVrResultProcessingTimeout) {
        if (isExternalURLSourcePresent) {
            trackTroError('Voice recording result processing timeout')
                .then(() => redirectToARwithError(isBenchmarkModeEnabled));
        } else {
            return (
                <HelpPopup
                    title={VR_RESULT_PROCESSING_TIMEOUT_TITLE}
                    problem={VR_RESULT_PROCESSING_TIMOUT_MESSAGE}
                    buttons={[
                        <LogoutButton
                            key="log-out"
                            className={COMMON_HELP_POPUP_BUTTON_CLASSNAME}
                            onClick={logUserOut}
                            isSecondary
                        />,
                    ]}
                />
            );
        }
    }

    if (isAudioSavingInProgress) {
        return (
            <HelpPopup
                icon={SPINNER_ICON}
                problem={AUDIO_SAVING_MESSAGE}
                title={VR_RESULT_PROCESSING_TITLE}
            />
        );
    }

    if (getShouldInvalidateResult(resultsVRStatuses, resultVRErrorReasons)) {
        if (isExternalURLSourcePresent) {
            trackTroError('Should invalidate result based on voice recording')
                .then(() => redirectToARwithError(isBenchmarkModeEnabled));
        }
        else {
            const isVRResultUnknownError = resultVRErrorReasons.includes(VR_ERROR_REASONS.UNKNOWN);
            return (
                <HelpPopup
                    problem={isVRResultUnknownError ? SOMETHING_WENT_WRONG_MESSAGE : UNABLE_TO_DETECT_READING_MESSAGE}
                    buttons={[
                        <LogoutButton
                            key="log-out"
                            className={COMMON_HELP_POPUP_BUTTON_CLASSNAME}
                            onClick={logUserOut}
                            isSecondary
                        />,
                    ]}
                >
                    <div>{isVRResultUnknownError ? INVALIDATE_RESULT_MESSAGE : INVALIDATE_RESULT_MESSAGE_WITH_REMINDER}</div>
                </HelpPopup>
            );
        }

    }

    return (
        <HelpPopup
            icon={SPINNER_ICON}
            problem={VR_RESULT_PROCESSING_MESSAGE}
            title={VR_RESULT_PROCESSING_TITLE}
        />
    );
};

VRHelpPopup.propTypes = {
    logUserOut: PropTypes.func.isRequired,
    isAudioSavingInProgress: PropTypes.bool,
    isVrResultProcessingTimeout: PropTypes.bool,
    isExternalURLSourcePresent: PropTypes.bool,
    isBenchmarkModeEnabled: PropTypes.bool,
    resultsVRStatuses: PropTypes.arrayOf(PropTypes.string).isRequired,
    resultVRErrorReasons: PropTypes.arrayOf(PropTypes.string).isRequired,
    trackTroError: PropTypes.func,
    bookId: PropTypes.string,
    bookTitle: PropTypes.string,
    trackTroVrPopup: PropTypes.func,
};

VRHelpPopup.defaultProps = {
    isAudioSavingInProgress: false,
    isVrResultProcessingTimeout: false,
    isExternalURLSourcePresent: false,
    isBenchmarkModeEnabled: false,
    bookId: null,
    bookTitle: null,
    trackTroError: () => {},
    trackTroVrPopup: () => {},
};

export default VRHelpPopup;
